import {
  SHOW_AUTOMATON_SECTION_MAX_ZOOM,
  SHOW_AUTOMATON_SECTION_MID_ZOOM,
  SHOW_AUTOMATON_SECTION_MIN_ZOOM,
  SHOW_PERIMETER_PORTAL_MIN_ZOOM,
} from '@utils/map/mapZoom.constants';
import { PERIMETER_SECTION_TEXT_OFFSET_L, SENSOR_LABEL_TEXT_SIZE } from '@utils/sensors/configuration.constants';
import { CircleLayerSpecification, LineLayerSpecification, SymbolLayerSpecification } from 'mapbox-gl';

import { FeatureTypeEnum, LayerNameEnum, SourceNameEnum } from '@/types/map.types';
import { SENSOR_LABEL_TEXT_OFFSET_PX_L } from '@/types/sensor/sensor.types';

const segmentMinSize = 4;
const segmentMaxSize = 6;

export const automatonSectionLayer: LineLayerSpecification = {
  id: LayerNameEnum.AUTOMATON_SECTIONS,
  type: 'line',
  source: SourceNameEnum.AUTOMATON_SECTION_LINES,
  minzoom: SHOW_AUTOMATON_SECTION_MIN_ZOOM,
  filter: ['==', 'featureType', FeatureTypeEnum.AUTOMATON_SECTION_LINE],
  paint: {
    'line-color': ['get', 'color'],
    'line-width': ['step', ['zoom'], segmentMinSize, SHOW_AUTOMATON_SECTION_MAX_ZOOM, segmentMaxSize],
  },
};

export const automatonSectionOutlineLayer: LineLayerSpecification = {
  id: LayerNameEnum.AUTOMATON_SECTION_OUTLINES,
  type: 'line',
  source: SourceNameEnum.AUTOMATON_SECTION_LINES,
  minzoom: SHOW_AUTOMATON_SECTION_MIN_ZOOM,
  filter: ['==', 'featureType', FeatureTypeEnum.AUTOMATON_SECTION_LINE],
  paint: {
    'line-color': 'black',
    'line-width': ['step', ['zoom'], segmentMinSize + 4, SHOW_AUTOMATON_SECTION_MAX_ZOOM, segmentMaxSize + 4],
  },
};

export const automatonSectionExtremityLayer: CircleLayerSpecification = {
  id: LayerNameEnum.AUTOMATON_SECTION_EXTREMITIES,
  type: 'circle',
  source: SourceNameEnum.AUTOMATON_SECTION_LINES,
  minzoom: SHOW_AUTOMATON_SECTION_MIN_ZOOM,
  filter: ['==', 'featureType', FeatureTypeEnum.AUTOMATON_SECTION_POINT],
  paint: {
    'circle-color': ['get', 'color'],
    'circle-radius': ['step', ['zoom'], segmentMinSize, SHOW_AUTOMATON_SECTION_MAX_ZOOM, segmentMaxSize],
    'circle-stroke-color': 'black',
    'circle-stroke-width': 2,
  },
};

export const automatonSectionIconLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.AUTOMATON_SECTION_ICONS,
  type: 'symbol',
  source: SourceNameEnum.AUTOMATON_SECTION_ICONS,
  minzoom: SHOW_AUTOMATON_SECTION_MID_ZOOM,
  filter: ['==', 'featureType', FeatureTypeEnum.AUTOMATON_SECTION_ICON],
  layout: {
    'icon-image': [
      'step',
      ['zoom'],
      ['concat', ['get', 'icon'], '-S'],
      SHOW_AUTOMATON_SECTION_MAX_ZOOM,
      ['concat', ['get', 'icon'], '-L'],
    ],
    'icon-anchor': 'center',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-pitch-alignment': 'viewport',
    'icon-rotation-alignment': 'viewport',
    'symbol-z-order': 'source',
  },
};

export const automatonSectionIconLabelLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.AUTOMATON_SECTION_LABELS,
  type: 'symbol',
  source: SourceNameEnum.AUTOMATON_SECTION_ICONS,
  minzoom: SHOW_AUTOMATON_SECTION_MAX_ZOOM,
  filter: ['==', ['get', 'featureType'], FeatureTypeEnum.AUTOMATON_SECTION_LABEL],
  layout: {
    'icon-image': 'icSymb-label',
    'icon-size': 1,
    'icon-anchor': 'center',
    'icon-allow-overlap': true,
    'icon-offset': PERIMETER_SECTION_TEXT_OFFSET_L,
    'text-font': ['roboto'],
    'text-field': ['get', 'textField'],
    'text-size': SENSOR_LABEL_TEXT_SIZE,
    'text-allow-overlap': true,
    'text-offset': SENSOR_LABEL_TEXT_OFFSET_PX_L,
    'symbol-z-order': 'source',
  },
  paint: {
    'text-color': 'white',
  },
};

export const perimeterPortalLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.PERIMETER_PORTALS,
  type: 'symbol',
  source: SourceNameEnum.AUTOMATON_SECTION_ICONS,
  minzoom: SHOW_PERIMETER_PORTAL_MIN_ZOOM,
  filter: ['==', 'featureType', FeatureTypeEnum.PERIMETER_PORTAL],
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-anchor': 'center',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-pitch-alignment': 'viewport',
    'icon-rotation-alignment': 'viewport',
    'symbol-z-order': 'source',
  },
};
